import React, {memo, FC, useEffect, useState} from "react";
import {FormikProps, FieldArray} from "formik";
import {Alert, Button, Modal, Form, Tooltip, Switch} from "antd";
import {InfoCircleTwoTone} from "@ant-design/icons";
import {blue} from "@ant-design/colors";
import {ApolloError} from "@apollo/client";
import moment from "moment";
import i18n from "../../services/i18n";
import {
    NewCourseInput,
    EditCourseInput,
    useGetInstallmentPlanQuery,
    NewInstallmentPlanInput,
    PaymentType,
    PaymentInterval,
    CreateInstallmentRateByCourseCreateInput,
    Maybe,
    CollitionCheckInput,
    useGetMultipleCourseLessonsCollisionCheckQuery,
    GetMultipleCourseLessonsCollisionCheckQuery,
    useGetHolidaysByCompanyAndDateRangeQuery,
    useGetCompanyInfoQuery,
    GetHolidaysByCompanyAndDateRangeQuery,
} from "../../generated/graphql";
import CourseCreateDateList from "./CourseCreateDateList";
import LayoutStep from "../../views/LayoutStep/LayoutStep";
import SectionHeader from "../../views/SectionHeader/SectionHeader";
import LayoutTableWrapper from "../../views/LayoutTableWrapper/LayoutTableWrapper";
import {handleDefaultDateChange, handleDefaultCourseCycleChange, handleDefaultTimeChange, generateLessons, addLesson, getLessonGenerationRange} from "./courseHelpers";
import {CourseNumberOfLessonsInput} from "./CourseViews/CourseStepViews";
import CourseDateInput from "../../views/CourseDateInput/CourseDateInput";
import {createInputHelpers} from "../../helpers/createInputHelpers";
import CourseCycleInput from "../../views/CourseCycleInput/CourseCycleInput";
import CourseTimeInput from "../../views/CourseTimeInput/CourseTimeInput";
import TimeInputError from "./CourseEdit/TimeInputError";
import Description from "./CourseViews/Description";
import {getlessonId} from "./helper/getLessonId";
import LessonBookingBadge from "../common/LessonBookingBadge";

type PossibleFormikPropsType = NewCourseInput | EditCourseInput;

type StepCourseLessonsProps = {
    formikProps: FormikProps<PossibleFormikPropsType>;
    error: ApolloError | Error | undefined;
    courseNumber?: string;
    nextStepButton: React.ReactElement;
    subtitle?: Maybe<string> | React.ReactNode;
    style?: React.CSSProperties;
    hasBilledLessons?: boolean;
    billedLessonIds?: string[];
    // Optional von außen gesteuert (Kurs kopieren teilt den Schalter mit dem Kopier-Hinweis)
    skipHolidays?: boolean;
    onSkipHolidaysChange?: (skipHolidays: boolean) => void;
};

const StepCourseLessons: FC<StepCourseLessonsProps> = ({
    formikProps,
    error,
    courseNumber,
    nextStepButton,
    subtitle,
    style,
    hasBilledLessons = false,
    billedLessonIds = [],
    skipHolidays: controlledSkipHolidays,
    onSkipHolidaysChange,
}) => {
    const [hasBeenEdited, setHasBeenEdited] = useState<boolean>(false);
    const {values} = formikProps;
    const {help, validateStatus} = createInputHelpers<PossibleFormikPropsType>(formikProps);
    const [timeError, setTimeError] = useState<string | undefined>(undefined);
    const [holidays, setHolidays] = useState<GetHolidaysByCompanyAndDateRangeQuery["holidaysByCompanyAndDateRange"]>([]);
    // Nur Formularzustand, wird nicht gespeichert. Standardmäßig aus, sonst fallen Ferienkurse komplett weg (Support mainzersv01).
    const [ownSkipHolidays, setOwnSkipHolidays] = useState<boolean>(false);
    const skipHolidays = controlledSkipHolidays ?? ownSkipHolidays;
    const setSkipHolidays = onSkipHolidaysChange ?? setOwnSkipHolidays;

    useEffect(() => {
        if (formikProps.values === formikProps.initialValues) {
            setHasBeenEdited(false);
        } else {
            setHasBeenEdited(true);
        }
    }, [formikProps]);

    // HANDLE COLLISIONS --------------------------------------------------------------------------
    const lessons = values.lessons;
    const getInput = (): Array<CollitionCheckInput> => {
        return lessons.map((lesson, index) => {
            return {
                venueId: lesson.venueId,
                courseLessonId: getlessonId(lesson),
                courseLessonIndex: index,
                startDateTime: lesson.startDateTime,
                endDateTime: lesson.endDateTime,
            };
        }) as Array<CollitionCheckInput>;
    };
    const collitionCheckInput = getInput();
    const {
        data: collisionData,
        // loading: collisionLoading,
        // error: collisionError,
    } = useGetMultipleCourseLessonsCollisionCheckQuery({
        skip: collitionCheckInput.length === 0,
        variables: {
            collitionCheckInput,
        },
        fetchPolicy: "network-only",
    });

    const collitions: GetMultipleCourseLessonsCollisionCheckQuery["multipleCourseLessonsCollisionCheck"] = collisionData?.multipleCourseLessonsCollisionCheck ?? [];

    // HANDLE HOLIDAYS ---------------------------------------------------------------------------------

    // Zeitraum = vorhandene Termine plus geschätzter Zeitraum fürs Neu-Erzeugen,
    // damit die Ferien schon vor dem Klick auf „neu generieren“ bekannt sind
    const generationRange = getLessonGenerationRange(values);
    const lessonDates = values.lessons.flatMap((lesson) => [moment(lesson.startDateTime), moment(lesson.endDateTime)]);
    const start = moment.min(moment(generationRange.start), ...lessonDates).startOf("day").format();
    // Ein Jahr Puffer, damit auch „Kurseinheit hinzufügen“ hinter dem letzten Termin Ferien kennt
    const end = moment.max(moment(generationRange.end), ...lessonDates).add(1, "year").endOf("day").format();

    const {data: holidayData, loading: loadingHoldiays} = useGetHolidaysByCompanyAndDateRangeQuery({
        variables: {
            start,
            end,
        },
    });

    useEffect(() => {
        if (holidayData && !loadingHoldiays) {
            setHolidays(holidayData.holidaysByCompanyAndDateRange);
        }
    }, [holidayData, loadingHoldiays]);

    // Schalter immer zeigen, wenn der Tenant die Ferien-Funktion hat, auch ohne Ferien im aktuellen Zeitraum
    const {data: companyInfoData} = useGetCompanyInfoQuery({fetchPolicy: "cache-first"});
    const showSkipHolidays = companyInfoData?.companyInfo?.subscription?.holidays === true;
    // Direkt aus dem Abfrageergebnis, der State hinkt nach einem Variablenwechsel einen Render hinterher
    const currentHolidays = holidayData?.holidaysByCompanyAndDateRange ?? [];
    const regenerateLessons = () => generateLessons(formikProps, skipHolidays ? currentHolidays : []);

    // HANDLE INSTALLMENT PLAN --------------------------------------------------------------------------

    const [currentInstallmentRates, setCurrentInstallmentRates] = useState<Array<CreateInstallmentRateByCourseCreateInput> | undefined>(formikProps.values.installmentRates ?? undefined);

    const getNewInstallmentPlanInput = (formikProps: FormikProps<PossibleFormikPropsType>): NewInstallmentPlanInput => {
        const {lessons, monthlySchedule, paymentInterval, vat, fee} = values;
        const sortedInstallmentRates = values.installmentRates?.slice().sort((a, b) => a.installmentDue - b.installmentDue);
        const dateOfFirstInstallment = sortedInstallmentRates?.[0]?.installmentDue;
        const courseFee = fee ?? 0;
        const courseVat = vat ?? 0;

        const grossPrice = paymentInterval === "PerCourse" ? courseFee : courseFee * lessons.length;

        const newInstallmentPlanInput: NewInstallmentPlanInput = {
            coursePrice: grossPrice,
            vat: courseVat,
            paymentInterval: paymentInterval ?? PaymentInterval.PerCourse,
            // Zahlungsart des Kurses, sonst erhält jeder Vorkasse-Kurs beim Bearbeiten einen Monatsplan
            paymentType: values.paymentType ?? PaymentType.Monthly,
            monthlySchedule,
            courseLessonStartDates: lessons.map((lesson) => lesson.startDateTime),
            dateOfFirstInstallment,
        };

        return newInstallmentPlanInput;
    };

    const {data: installmentPlanData} = useGetInstallmentPlanQuery({
        variables: {
            newInstallmentPlanInput: getNewInstallmentPlanInput(formikProps),
        },
        fetchPolicy: "network-only",
    });

    const installmentRates = installmentPlanData?.getInstallmentPlanPreview?.schedule;

    useEffect(() => {
        if (installmentRates && installmentRates !== currentInstallmentRates) {
            setCurrentInstallmentRates(installmentRates);
            formikProps.setFieldValue("installmentRates", installmentRates);
        }
    }, [currentInstallmentRates, formikProps, installmentRates]);

    const lessonCountInputError = values.defaultLessonCount === 0 || values.defaultLessonCount === null;

    // HANDLE LESSONS ----------------------------------------------------------------------------------

    const editorStyle: React.CSSProperties = {
        gridColumn: "1 / span 4",
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gridGap: "1rem",
        backgroundColor: "#f0f0f0",
        padding: "1rem",
        ...(hasBilledLessons
            ? {
                  opacity: 0.6,
                  pointerEvents: "none" as const,
                  cursor: "not-allowed" as const,
              }
            : {}),
    };

    return (
        <LayoutStep columns={4} title={i18n.containers.courses.CourseCreate.courseStepLessons.title()} subtitle={subtitle} error={error} button={nextStepButton} style={style}>
            {hasBilledLessons && (
                <Alert type="info" showIcon message={i18n.containers.courses.CourseEdit.billedLessons.settingsDisabled.basicSettings()} style={{gridColumn: "1 / span 4", marginBottom: "1rem"}} />
            )}
            <div style={editorStyle}>
                <SectionHeader level={2} text={i18n.containers.courses.CourseCreate.courseStepLessons.sections.settings()} inlineContent={<Description />} />
                <CourseDateInput
                    label={i18n.containers.courses.CourseCreate.firstCourseLessonDate.label()}
                    value={values.startDateTime ?? undefined}
                    onChange={handleDefaultDateChange(formikProps)}
                    validateStatus={validateStatus("startDateTime")}
                    help={help("startDateTime")}
                    testId="CourseDateInput"
                />

                <CourseCycleInput
                    label={i18n.containers.courses.CourseCreate.courseCycle.label()}
                    placeholder={i18n.containers.courses.CourseCreate.courseCycle.placeholder()}
                    value={values.cycle ?? undefined}
                    onChange={handleDefaultCourseCycleChange(formikProps)}
                    validateStatus={validateStatus("cycle")}
                    help={help("cycle")}
                    testId="CourseCycleInput"
                />
                <CourseTimeInput
                    label={i18n.containers.courses.CourseCreate.courseStartDateTime.label()}
                    placeholder={i18n.containers.courses.CourseCreate.courseStartDateTime.placeholder()}
                    value={values.startDateTime ?? undefined}
                    onChange={handleDefaultTimeChange(formikProps, "startDateTime")}
                    validateStatus={validateStatus("startDateTime")}
                    help={help("startDateTime")}
                    testId="CourseStartDateTimeInput"
                />

                <CourseTimeInput
                    label={i18n.containers.courses.CourseCreate.courseEndDateTime.label()}
                    placeholder={i18n.containers.courses.CourseCreate.courseEndDateTime.placeholder()}
                    value={values.endDateTime ?? undefined}
                    onChange={handleDefaultTimeChange(formikProps, "endDateTime")}
                    validateStatus={timeError as any}
                    help={<TimeInputError startDateTime={values.startDateTime} endDateTime={values.endDateTime} setTimeError={setTimeError} />}
                    testId="CourseEndDateTimeInput"
                />
                <CourseNumberOfLessonsInput label={i18n.containers.courses.CourseCreate.courseNumberOfLessons.label()} formikProps={formikProps} testId="CourseNumberOfLessonsInput" />
                {showSkipHolidays && (
                    // Freie vierte Spalte neben „Anzahl Kurseinheiten“
                    <Form.Item
                        label={
                            // Inline statt Space, damit das Icon beim Umbruch am letzten Wort bleibt
                            <span>
                                {i18n.containers.courses.SkipHolidays.label()}{" "}
                                <Tooltip title={i18n.containers.courses.SkipHolidays.description()}>
                                    <InfoCircleTwoTone twoToneColor={blue[5]} />
                                </Tooltip>
                            </span>
                        }
                    >
                        <Switch checked={skipHolidays} onChange={setSkipHolidays} data-testid="SkipHolidaysSwitch" />
                    </Form.Item>
                )}
                <div
                    style={{
                        gridColumn: "1 / span 4",
                    }}
                >
                    <Tooltip
                        title={
                            timeError
                                ? i18n.containers.courses.CourseCreate.courseEndDateTime.endTimeBeforeStart()
                                : lessonCountInputError
                                ? i18n.containers.courses.CourseCreate.defaultLessonCount.errorMsg()
                                : undefined
                        }
                    >
                        <Button
                            block
                            // type="primary"
                            type={hasBeenEdited ? "primary" : "default"}
                            disabled={timeError !== undefined || lessonCountInputError || loadingHoldiays}
                            onClick={() => {
                                if (courseNumber) {
                                    Modal.confirm({
                                        icon: null,
                                        style: {whiteSpace: "pre-line"},
                                        title: i18n.containers.courses.CourseCreate.modals.recalculateLessons.title(),
                                        content: i18n.containers.courses.CourseCreate.modals.recalculateLessons.description(),
                                        okText: i18n.containers.courses.CourseCreate.modals.recalculateLessons.ok(),
                                        cancelText: i18n.containers.courses.CourseCreate.modals.recalculateLessons.cancel(),
                                        onOk: regenerateLessons,
                                    });
                                } else {
                                    regenerateLessons();
                                }
                            }}
                        >
                            {i18n.containers.courses.CourseCreate.courseStepLessons.recalculateButton()}
                        </Button>
                    </Tooltip>
                </div>
            </div>
            <SectionHeader level={2} text={i18n.containers.courses.CourseCreate.courseStepLessons.sections.lessons()} style={{marginTop: "1rem"}} />
            <LayoutTableWrapper>
                <FieldArray
                    name="lessons"
                    render={({remove}) => {
                        return (
                            <>
                                <CourseCreateDateList
                                    data-testid="CourseLessonTable"
                                    formikProps={formikProps}
                                    handleRemove={remove}
                                    collitions={collitions}
                                    holidays={holidays}
                                    billedLessonIds={billedLessonIds}
                                />
                                <Form.Item style={{gridColumn: "1 / -1"}}>
                                    <Button
                                        data-testid="CourseLessonAddButton"
                                        size="small"
                                        style={{width: "100%"}}
                                        type="dashed"
                                        onClick={() => addLesson(formikProps, skipHolidays ? currentHolidays : [])}
                                        disabled={formikProps.values.lessons.length >= 53 || loadingHoldiays}
                                    >
                                        {i18n.containers.courses.CourseCreate.addLesson()}
                                    </Button>
                                </Form.Item>
                            </>
                        );
                    }}
                />
            </LayoutTableWrapper>
            {formikProps.values.allowLessonBooking && <LessonBookingBadge />}
        </LayoutStep>
    );
};

export default memo(StepCourseLessons);
